import type enDict from '../../i18n/en.json';

/** Shape of every dictionary; `en.json` is the reference the build checks against. */
export type Dictionary = typeof enDict;
export type Locale = Dictionary['lang'];

const LOCALES = ['en', 'vi'] as const;
const DEFAULT_LOCALE: Locale = 'en';

let active: Dictionary | null = null;

/** The locale this page was generated for, taken from <html lang>. */
export function pageLocale(): Locale {
  const lang = document.documentElement.lang as Locale;
  return (LOCALES as readonly string[]).includes(lang) ? lang : DEFAULT_LOCALE;
}

/**
 * One lazy chunk per dictionary, discovered at build time. `import.meta.glob`
 * rather than a template-literal import: Vite can analyse this, so each
 * dictionary is really emitted as its own chunk.
 */
const DICTIONARIES = import.meta.glob<{ default: Dictionary }>('../../i18n/*.json');

/**
 * Loads the dictionary for this page. A visitor only ever downloads the
 * language they are actually reading.
 */
export async function initI18n(): Promise<Dictionary> {
  const locale = pageLocale();
  const load = DICTIONARIES[`../../i18n/${locale}.json`];
  if (!load) throw new Error(`No dictionary for locale "${locale}".`);
  active = (await load()).default;
  return active;
}

/**
 * The loaded dictionary. Throws rather than returning placeholder text, so a
 * missing `initI18n()` fails at the first call instead of shipping English
 * strings onto a Vietnamese page.
 */
export function tr(): Dictionary {
  if (!active) throw new Error('initI18n() must be awaited before tr() is used.');
  return active;
}

/**
 * Fills `{name}` placeholders: format('Turn: {side}', { side: 'White' }).
 *
 * Substitutes verbatim, so the result belongs in `textContent`. For a dictionary
 * string that is deliberately markup and goes into `innerHTML`, use formatHtml.
 */
export function format(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * format() for a template written into `innerHTML`. The dictionary's own markup
 * (a `<b>` around the player's name) survives; every substituted value is escaped,
 * so the day a value comes from a child typing a nickname rather than from the
 * dictionary, it still cannot become markup.
 */
export function formatHtml(template: string, values: Record<string, string | number>): string {
  const escaped = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      String(value).replace(/[&<>"']/g, (character) => HTML_ESCAPES[character]),
    ]),
  );
  return format(template, escaped);
}
